/**
 * Canonical Gerstner wave field shared by the Colyseus server (CPU buoyancy) and
 * the Three.js client (GPU displacement).
 *
 * One table feeds both sides: `sampleHeight`/`sampleNormal` are the CPU truth and
 * `buildGerstnerGLSL` prints the exact same constants into shader source, so the
 * physics and the visuals cannot drift.
 *
 * Determinism: this module must never use `Math.random`. The table is a fixed
 * canonical constant and every derived value is pure arithmetic.
 */

export interface WaveComponent {
  /** Normalised travel direction on the water plane (x). */
  dirX: number;
  /** Normalised travel direction on the water plane (z). */
  dirZ: number;
  /** Wavelength in metres. */
  wavelength: number;
  /** Peak amplitude in metres. */
  amplitude: number;
  /** Gerstner steepness (0 = trochoidal-free, 1 = sharp cusps). */
  steepness: number;
  /** Phase speed in metres/second. */
  speed: number;
}

export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

/**
 * The single canonical ocean table. A long primary swell with a few shorter
 * trains crossing it — enough shape to make pitch/roll readable without
 * swamping the deck.
 */
export const WAVE_TABLE: readonly WaveComponent[] = [
  // Primary swell — dominant direction (+X)
  { dirX: 1.0, dirZ: 0.15, wavelength: 46.0, amplitude: 0.72, steepness: 0.62, speed: 4.6 },
  // Secondary swell — angled cross-sea
  { dirX: 0.72, dirZ: 0.69, wavelength: 29.0, amplitude: 0.44, steepness: 0.55, speed: 3.9 },
  // Opposing swell — comes from behind, breaks up the tiling
  { dirX: -0.6, dirZ: -0.8, wavelength: 34.0, amplitude: 0.22, steepness: 0.38, speed: 4.1 },
  // Cross chop — perpendicular to primary
  { dirX: 0.15, dirZ: -0.99, wavelength: 13.0, amplitude: 0.14, steepness: 0.40, speed: 2.7 },
  // Medium crossing wave
  { dirX: -0.45, dirZ: 0.89, wavelength: 11.5, amplitude: 0.15, steepness: 0.42, speed: 2.5 },
  // Short chop A — high frequency detail
  { dirX: 0.87, dirZ: -0.5, wavelength: 5.2, amplitude: 0.06, steepness: 0.30, speed: 1.7 },
  // Short chop B — opposing high frequency detail
  { dirX: -0.7, dirZ: 0.71, wavelength: 3.8, amplitude: 0.04, steepness: 0.25, speed: 1.4 },
];

/** Wavenumber k = 2π / wavelength. */
function wavenumber(component: WaveComponent): number {
  return (2 * Math.PI) / component.wavelength;
}

/**
 * Normalise a table: direction vectors with unit length and validated finite
 * components. Kept pure so both CPU and GLSL derive from the same values.
 */
export function normalizeWaveTable(table: readonly WaveComponent[]): WaveComponent[] {
  return table.map((component) => {
    const length = Math.hypot(component.dirX, component.dirZ) || 1;
    return {
      dirX: component.dirX / length,
      dirZ: component.dirZ / length,
      wavelength: component.wavelength,
      amplitude: component.amplitude,
      steepness: component.steepness,
      speed: component.speed,
    };
  });
}

/** Canonical, normalised table (the default the server ships to clients). */
export const NORMALIZED_WAVE_TABLE: readonly WaveComponent[] = normalizeWaveTable(WAVE_TABLE);

/**
 * CPU Gerstner height at an undisplaced point. Mirrors the GLSL `waveDisp().y`
 * component exactly (the horizontal Gerstner slide stays within hull width, so
 * sampling the undisplaced point is the standard buoyancy approximation).
 */
export function sampleHeight(
  x: number,
  z: number,
  t: number,
  amplitudeScale = 1,
  table: readonly WaveComponent[] = NORMALIZED_WAVE_TABLE,
): number {
  let height = 0;
  for (const component of table) {
    const k = wavenumber(component);
    const theta = k * (component.dirX * x + component.dirZ * z - component.speed * t);
    height += component.amplitude * Math.sin(theta);
  }
  return height * amplitudeScale;
}

/**
 * CPU surface slope → unit normal. Mirrors the GLSL `waveSlope` (the analytic
 * derivative of the height), returned as a plain object so the server does not
 * need to depend on Three.js.
 */
export function sampleNormal(
  x: number,
  z: number,
  t: number,
  amplitudeScale = 1,
  table: readonly WaveComponent[] = NORMALIZED_WAVE_TABLE,
): Vec3Like {
  let gx = 0;
  let gz = 0;
  for (const component of table) {
    const k = wavenumber(component);
    const theta = k * (component.dirX * x + component.dirZ * z - component.speed * t);
    const cos = Math.cos(theta);
    gx += component.amplitude * k * component.dirX * cos;
    gz += component.amplitude * k * component.dirZ * cos;
  }
  const nx = -gx * amplitudeScale;
  const nz = -gz * amplitudeScale;
  const length = Math.hypot(nx, 1, nz) || 1;
  return { x: nx / length, y: 1 / length, z: nz / length };
}

/** Serialise a table into a plain, transferable numeric form. */
export function serializeWaveTable(
  table: readonly WaveComponent[] = NORMALIZED_WAVE_TABLE,
): WaveComponent[] {
  return normalizeWaveTable(table);
}

function fixed(value: number): string {
  return value.toFixed(5);
}

/**
 * Build the GLSL source for the canonical wave field.
 *
 * Emits `uniform float uWaveAmp`, `vec3 waveDisp(vec2 p, float t)` and
 * `vec2 waveSlope(vec2 p, float t)`. The per-wave constants are printed on a
 * single, parseable line each so `scripts/check-wave-field.mjs` can reconstruct
 * the CPU result from the generated GLSL and assert the two agree.
 */
export function buildGerstnerGLSL(table: readonly WaveComponent[] = NORMALIZED_WAVE_TABLE): string {
  const components = normalizeWaveTable(table);

  const displacementLines = components
    .map((component) => {
      const k = wavenumber(component);
      const qa = component.steepness * component.amplitude;
      return (
        `  { float th = ${fixed(k)}*(dot(vec2(${fixed(component.dirX)}, ${fixed(component.dirZ)}), p) - ${fixed(component.speed)}*t); ` +
        `d.y += ${fixed(component.amplitude)}*sin(th); ` +
        `d.xz += ${fixed(qa)}*vec2(${fixed(component.dirX)}, ${fixed(component.dirZ)})*cos(th); }`
      );
    })
    .join('\n');

  const slopeLines = components
    .map((component) => {
      const k = wavenumber(component);
      const ak = component.amplitude * k;
      return (
        `  { float th = ${fixed(k)}*(dot(vec2(${fixed(component.dirX)}, ${fixed(component.dirZ)}), p) - ${fixed(component.speed)}*t); ` +
        `g += ${fixed(ak)}*vec2(${fixed(component.dirX)}, ${fixed(component.dirZ)})*cos(th); }`
      );
    })
    .join('\n');

  return [
    'uniform float uWaveAmp;',
    '',
    'vec3 waveDisp(vec2 p, float t) {',
    '  vec3 d = vec3(0.0);',
    displacementLines,
    '  return d * uWaveAmp;',
    '}',
    '',
    'vec2 waveSlope(vec2 p, float t) {',
    '  vec2 g = vec2(0.0);',
    slopeLines,
    '  return g * uWaveAmp;',
    '}',
    '',
  ].join('\n');
}
